import { Clipboard } from '@capacitor/clipboard';
import { useIonToast } from '@ionic/react';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { setMe } from '@actions/sessionActions';
import ExpandableSection from '@components/animations/ExpandableSection';
import ActionIconButton from '@components/buttons/ActionIconButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import ClientAvatar, { Color } from '@components/ClientAvatar';
import AccountForm from '@components/forms/AccountForm';
import InviteAccountMemberForm from '@components/forms/InviteAccountMemberForm';
import EditableSection from '@components/layout/EditableSection';
import Modal, { ModalElement } from '@components/Modal';
import Nbsp from '@components/Nbsp';
import AlertPopover from '@components/popovers/AlertPopover';
import Popover from '@components/popovers/Popover';
import PopoverItem from '@components/popovers/PopoverItem';
import styles from '@components/profile/Profile.module.css';
import BodyCopy from '@components/typography/BodyCopy';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import { sessionContext } from '@context/SessionStore';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyClient from '@hooks/useMyClient';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import {
  CareTeamInviteCancelDocument,
  CareTeamInviteCancelMutation,
  CareTeamInviteCancelMutationVariables,
  CareTeamInviteResendDocument,
  CareTeamInviteResendMutation,
  CareTeamInviteResendMutationVariables,
  ClientRoleDestroyDocument,
  ClientRoleDestroyMutation,
  ClientRoleDestroyMutationVariables,
  ClientRoleRoleEnum,
  ClientUpdateMutation,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { IconList } from '@utils/iconUtils';

type Journey = GetJourneyWithCareProfileQuery['journey'];

type CareTeamInviteType = NonNullable<Journey['careTeamInvites']>[0];
type ClientResponse = NonNullable<NonNullable<ClientUpdateMutation['clientUpdate']>['client']>;

type Props = {
  journey: Journey;
};

type CareTeamInviteProps = {
  cti: CareTeamInviteType;
};

const CareTeamInvite = ({ cti }: CareTeamInviteProps) => {
  const popoverOpener = useOpener();

  const [cancelCareTeamInvite] = useMutationWithValidation<
    CareTeamInviteCancelMutation,
    CareTeamInviteCancelMutationVariables
  >(CareTeamInviteCancelDocument, 'careTeamInvite', {
    update: (store, { data }) => {
      store.modify({
        fields: {
          canceled() {
            return true;
          }
        },
        id: store.identify({ ...data?.careTeamInviteCancel?.careTeamInvite })
      });
    }
  });

  const [resendCareTeamInvite] = useMutationWithValidation<
    CareTeamInviteResendMutation,
    CareTeamInviteResendMutationVariables
  >(CareTeamInviteResendDocument, 'careTeamInvite');

  const intl = useIntl();
  const fiveMinutesInMs = 5 * 60000;
  const [presentToast, dismissToast] = useIonToast();

  const handleResendCareTeamInvite = useCallback(
    (invitation: CareTeamInviteType) => {
      if (invitation.lastSentAt && new Date().getTime() - invitation.lastSentAt.getTime() > fiveMinutesInMs) {
        resendCareTeamInvite({ id: invitation.id }).then(response => {
          if (response?.careTeamInviteResend?.careTeamInvite) {
            presentToast({
              buttons: [{ handler: () => dismissToast(), icon: IconList.xmark }],
              cssClass: styles.toast,
              duration: 5000,
              message: intl.formatMessage({ id: 'pages.client.careProfile.careTeam.resentToast' }),
              position: 'top'
            });
          }

          popoverOpener.close();
        });
      } else {
        presentToast({
          buttons: [{ handler: () => dismissToast(), icon: IconList.xmark }],
          cssClass: styles.toastWider,
          duration: 5000,
          message: intl.formatMessage({ id: 'pages.client.careProfile.careTeam.resentInvalidToast' }),
          position: 'top'
        });
        popoverOpener.close();
      }
    },
    [fiveMinutesInMs, resendCareTeamInvite, popoverOpener, presentToast, intl, dismissToast]
  );

  const handleCancelCareTeamInvite = (invitation: CareTeamInviteType) => {
    cancelCareTeamInvite({ id: invitation.id });
    popoverOpener.close();
  };

  const handleCopyInvite = useCallback(
    (invitation: CareTeamInviteType) => {
      if (invitation.link) {
        Clipboard.write({ string: invitation.link });
        popoverOpener.close();
      }
    },
    [popoverOpener]
  );

  return (
    <div className={styles.role}>
      <div className={styles.avatarAndName}>
        <ClientAvatar
          bold
          clientId={null}
          color="neutral100"
          firstName={cti.email}
          fontSize={20}
          journeyId={null}
          size={38}
        />
        <BodyCopy color="neutral200">
          {cti.email} <Nbsp /> <FormattedMessage id="pages.profile.pending" />
        </BodyCopy>
      </div>
      <Popover isOpen={popoverOpener.isOpen} opener={popoverOpener}>
        <PopoverItem
          icon="trash"
          onClick={() => handleCancelCareTeamInvite(cti)}
          textKey="pages.profile.cancelInvite"
        />
        <AlertPopover textKey="pages.client.careProfile.careTeam.copied">
          <PopoverItem
            icon="copy"
            onClick={() => handleCopyInvite(cti)}
            textKey="pages.client.careProfile.careTeam.copy"
          />
        </AlertPopover>
        <PopoverItem
          icon="repeat"
          noBorder
          onClick={() => handleResendCareTeamInvite(cti)}
          textKey="pages.profile.resendInvite"
        />
      </Popover>
    </div>
  );
};

type ClientRole = Journey['clientRoles'][0];

type SupportMemberProps = {
  canEdit: boolean;
  canRemove: boolean;
  color: Color;
  journey: Journey;
  role: ClientRole;
};

const SupportMember = ({ canEdit, canRemove, color, journey, role }: SupportMemberProps) => {
  const popoverOpener = useOpener();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const myClient = useMyClient();
  const accountOpener = useOpener();
  const detailsOpener = useOpener(!!myExpert);
  const verifyEmailModalOpener = useOpener();

  const { dispatch } = useContext(sessionContext);

  const accountFormModalRef = useRef<ModalElement>(null);

  const [destroyClientRole, apolloClient] = useMutationWithValidation<
    ClientRoleDestroyMutation,
    ClientRoleDestroyMutationVariables
  >(ClientRoleDestroyDocument, 'clientRoleDestroy', {
    update: () => {
      apolloClient.resetStore();
    }
  });

  const handleAccountFormError = useCallback(() => {
    if (accountFormModalRef.current) {
      accountFormModalRef.current.scrollToTop();
    }
  }, [accountFormModalRef]);

  const handleAccountFormSuccess = useCallback(
    (client: ClientResponse) => {
      accountOpener.close();
      popoverOpener.close();
      if (client.id === myClient?.id) {
        dispatch(setMe(client));
      }
      if (!client.email.verified) {
        setTimeout(() => verifyEmailModalOpener.open(), 500);
      }
    },
    [accountOpener, dispatch, myClient?.id, popoverOpener, verifyEmailModalOpener]
  );

  const relationshipDescription = useMemo(() => {
    if (role.relationship?.other) {
      return role.relationship.other;
    }

    if (role.relationship?.relationshipOption) {
      return intl.formatMessage(
        {
          id: `models.careProfile.relationshipInverse.${role.relationship.relationshipOption}`
        },
        { name: journey.careProfile.name }
      );
    }

    if (myExpert) {
      return intl.formatMessage({ id: 'dictionary.unknown' });
    }
  }, [intl, journey.careProfile.name, myExpert, role.relationship?.other, role.relationship?.relationshipOption]);

  return (
    <div className={`${styles.role} ${styles.added} ${detailsOpener.isOpen ? styles.expandedBorder : ''}`}>
      <div>
        <div className={styles.avatarAndName}>
          <ClientAvatar
            bold
            clientId={role.client.id}
            color={color}
            firstName={role.client.firstName}
            fontSize={20}
            journeyId={role.journey.id}
            size={38}
          />
          <div className={styles.avatarAndName}>
            <div>
              <div className={styles.tinySpacingBottom}>
                <BodyCopyBold color="neutral500">{role.client.firstName}</BodyCopyBold>
              </div>
              {relationshipDescription && (
                <div className={styles.tinySpacingBottom}>
                  <BodyCopy color="neutral500">{relationshipDescription}</BodyCopy>
                </div>
              )}
              <ExpandableSection initial={false} isOpen={detailsOpener.isOpen}>
                {canEdit && role.client.location.address && (
                  <div className={styles.tinySpacingBottom}>
                    <BodyCopy color="neutral500">{role.client.location.address}</BodyCopy>
                  </div>
                )}
                {canEdit && role.client.email.email && (
                  <div className={styles.tinySpacingBottom}>
                    <BodyCopy color="neutral500">{role.client.email.email}</BodyCopy>
                  </div>
                )}
                {canEdit && role.client.consultPhoneNumber?.formattedNumber && (
                  <div>
                    <BodyCopy color="neutral500">{role.client.consultPhoneNumber.formattedNumber}</BodyCopy>
                  </div>
                )}
              </ExpandableSection>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.avatarAndName}>
        {(canEdit || myExpert) && (
          <ActionIconButton
            ariaLabelKey="accessibility.ariaLabels.toggle"
            icon={detailsOpener.isOpen ? 'chevronUp' : 'chevronDown'}
            onClick={detailsOpener.toggle}
          />
        )}
        {(canRemove || canEdit) && (
          <Popover isOpen={popoverOpener.isOpen} opener={popoverOpener}>
            {canEdit && (
              <PopoverItem
                icon="edit"
                noBorder
                onClick={accountOpener.open}
                textKey={
                  myClient
                    ? 'pages.client.careProfile.careTeam.editMyDetails'
                    : 'pages.client.careProfile.careTeam.editDetails'
                }
              />
            )}
            {canRemove && (
              <PopoverItem
                icon="trash"
                noBorder
                onClick={() =>
                  destroyClientRole({
                    id: role.id
                  })
                }
                textKey="pages.client.careProfile.careTeam.remove"
              />
            )}
          </Popover>
        )}
      </div>

      <Modal
        opener={accountOpener}
        ref={accountFormModalRef}
        title={
          myClient?.id === role.client.id
            ? intl.formatMessage({ id: 'pages.profile.editMyAccount' })
            : intl.formatMessage({ id: 'pages.profile.editAccount' }, { name: role.client.fullName })
        }
      >
        {({ modalDisplayed }) => (
          <AccountForm
            autofocus={modalDisplayed}
            careProfileName={journey.careProfile.name}
            client={role.client}
            clientRole={role}
            onCancel={accountOpener.close}
            onError={handleAccountFormError}
            onSuccess={handleAccountFormSuccess}
          />
        )}
      </Modal>
    </div>
  );
};

const SupportTeam = ({ journey }: Props) => {
  const myExpert = useMyExpert();
  const myClient = useMyClient();
  const inviteOpener = useOpener();

  const ownerRole = journey.clientRoles.find(role => role.role === ClientRoleRoleEnum.owner);

  const myRole = journey.clientRoles.find(role => role.client.id === myClient?.id);

  return (
    <div>
      <EditableSection descriptionKey="pages.profile.supportTeamDescription" titleKey="pages.profile.supportTeamTitle">
        <div>
          {ownerRole && (
            <SupportMember
              canEdit={!!(myRole?.id === ownerRole.id || myExpert)}
              canRemove={false}
              color="secondary500"
              journey={journey}
              role={ownerRole}
            />
          )}
          {journey.clientRoles
            .filter(clientRole => clientRole.id !== ownerRole?.id)
            .map((clientRole, index) => (
              <SupportMember
                canEdit={!!(myRole?.id === clientRole.id || myExpert)}
                canRemove={
                  !!(myRole?.role === ClientRoleRoleEnum.owner || myExpert) &&
                  clientRole.role !== ClientRoleRoleEnum.owner
                }
                color={
                  index === 0
                    ? 'primary500'
                    : index === 1
                    ? 'primary700'
                    : index === 2
                    ? 'secondary700'
                    : 'secondary500'
                }
                journey={journey}
                key={clientRole.id}
                role={clientRole}
              />
            ))}
          {journey.careTeamInvites
            ?.filter(invite => !invite.accepted && !invite.canceled)
            .map(cti => <CareTeamInvite cti={cti} key={cti.id} />)}
        </div>
        {inviteOpener.isOpen ? (
          <div className={styles.inviteForm}>
            <div className={styles.fullWidth}>
              <InviteAccountMemberForm journey={journey} onSuccess={inviteOpener.close} />
            </div>
            <div className={styles.addIcon}>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.close"
                icon="times"
                onClick={inviteOpener.toggle}
              />
            </div>
          </div>
        ) : (
          <div className={styles.addSomeone}>
            <TertiaryButton
              icon="add"
              iconPosition="start"
              onClick={inviteOpener.toggle}
              textKey="pages.client.careProfile.careTeam.addSomeone"
            />
          </div>
        )}
      </EditableSection>
    </div>
  );
};

export default SupportTeam;
